import React, { useState, useEffect, useRef } from 'react';
import { Table, Button, Icon, Modal, Header, Message, Field, Input, Checkbox, Toast, Pagination, Items, Label } from 'react-frontier';
import { Dropdown } from 'semantic-ui-react';
import { Budget, UserBankAccount, SetLoading, AssetType, ExpenseRejectReason, PaymentMethod } from '../SiteClasses';
import { addCommas, bindClose, bindClick, bindFormChange, getAssetTypeIcon, getDepartmentName } from '../SiteUtil';
import { useUser } from '../hooks';
import { BudgetModal, InfoLog } from './index';
import { Link } from 'react-router-dom';
import UploadBox, { UploadBoxRef } from './UploadBox';
import UserAccess from '../UserAccess';
import API from '../API';
import classNames from 'classnames';
import moment from 'moment';

interface BudgetTableProps {
	dateId: number;
	eventName: string;
}

const PAGE_SIZE = 20;

var BudgetTable = (props: BudgetTableProps)=>{
	var { hasAccess } = useUser();
	var [budgets, setBudgets] = useState<Budget[]>([]);
	var [loading, setLoading] = useState<boolean>(true);
	var [page, setPage] = useState<number>(0);
	var [showBudgetModal, setShowBudgetModal] = useState<boolean>(false);
	var [selectedBudget, setSelectedBudget] = useState<Budget>(null);
	var [showInfoModal, setShowInfoModal] = useState<boolean>(false);
	var [showApproveModal, setShowApproveModal] = useState<boolean>(false);
	var [showRejectModal, setShowRejectModal] = useState<boolean>(false);
	var [showGenerateExpenseModal, setShowGenerateExpenseModal] = useState<boolean>(false);
	var [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
	var [showPaidModal, setShowPaidModal] = useState<boolean>(false);
	var [showApproveAllModal, setShowApproveAllModal] = useState<boolean>(false);
	var [showAccountsModal, setShowAccountsModal] = useState<boolean>(false);
	var [accounts, setAccounts] = useState<UserBankAccount[]>(null);
	var [showInfoLogModal, setShowInfoLogModal] = useState<boolean>(false);
	var [modalPrompts, setModalPrompts] = useState<string[]>(null);
	var [selectedFile, setSelectedFile] = useState<File>(null);
	var [fileTypeId, setFileTypeId] = useState<number>(null);
	var [rejectReasonId, setRejectReasonId] = useState<number>(null);
	var [rejectReasons, setRejectReasons] = useState<ExpenseRejectReason[]>([]);
	var [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
	var [selectedMethodId, setSelectedMethodId] = useState<number>(null);
	var uploadBoxRef = useRef<UploadBoxRef>(null);

	useEffect(()=>{
		getBudgets();
		API.getExpenseRejectReasons().then(res=>{
			if(!res.error) setRejectReasons(res.data);
		});
		API.getPaymentMethods().then(res=>{
			if(!res.error) setPaymentMethods(res.data);
		});
	}, [props.dateId]);

	var getBudgets = (get_page?: number)=>{
		var offset = (get_page > 0) ? Math.max(0, (get_page || page) * PAGE_SIZE) : 0;
		
		setLoading(true);
		API.getEventBudgets(props.dateId, PAGE_SIZE, offset).then(res=>{
			if(res.error) return Toast.error(res.message);
			setBudgets(res.data);
		}).catch(err=>{
			Toast.error('Hubo un error cargando los presupuestos (LCL-1)');
		}).finally(()=>{
			setLoading(false);
		});
	};

	var onPageChange = (page: number)=>{
		setLoading(true);
		setPage(page);
		getBudgets(page);
	};

	var onAddBudget = ()=>{
		setShowBudgetModal(true);
		setSelectedBudget(null);
	};

	var onEditBudget = (budget: Budget)=>{
		setSelectedBudget(budget);
		setShowBudgetModal(true);
	};

	var onBudgetCreatedInternal = (budget: Budget)=>{
		getBudgets(page);
		setShowBudgetModal(false);
		setSelectedBudget(null);
	};

	var onShowInfo = (budget: Budget)=>{
		setSelectedBudget(budget);
		setShowInfoModal(true);
	};

	var onShowApprove = (budget: Budget)=>{
		setSelectedBudget(budget);
		setShowApproveModal(true);
	};

	var onShowGenerateExpense = (budget: Budget)=>{
		setSelectedBudget(budget);
		setSelectedFile(null);
		setFileTypeId(null);
		setSelectedMethodId(null);
		if(uploadBoxRef.current){
			uploadBoxRef.current.clear();
		}
		setShowGenerateExpenseModal(true);
	};

	var onShowDelete = (budget: Budget)=>{
		setSelectedBudget(budget);
		setShowDeleteModal(true);
	};

	var onShowPaid = (budget: Budget)=>{
		setSelectedBudget(budget);
		setModalPrompts(null);
		setShowPaidModal(true);
	};

	var paidBudget = (setLoading: SetLoading)=>{
		setLoading(true);
		API.paidBudget(selectedBudget.budget_id).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success('Se ha marcado el presupuesto como pagado');

			setBudgets(prev => prev.map(b => 
				b.budget_id === selectedBudget.budget_id ? { ...b, paid: true } : b
			));
			setShowPaidModal(false);
			setSelectedBudget(null);
		}).catch(err=>{
			setModalPrompts(['Hubo un error inesperado marcando el presupuesto como pagado (LCL-PB-1)']);
		}).finally(()=>{
			setLoading(false);
		});
	};

	var onShowApproveAll = ()=>{
		setShowApproveAllModal(true);
	};

	var onShowInfoLog = (budget: Budget)=>{
		setSelectedBudget(budget);
		setShowInfoLogModal(true);
	};

	var approveBudget = (setLoading: SetLoading)=>{
		setLoading(true);
		API.approveBudget([selectedBudget.budget_id]).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success('Se ha aprobado el presupuesto');

			setBudgets(prev => prev.map(b => 
				b.budget_id === selectedBudget.budget_id ? { ...b, approved: true } : b
			));
			setShowApproveModal(false);
			setSelectedBudget(null);
		}).catch(err=>{
			setModalPrompts(['Hubo un error inesperado actualizando el presupuesto (LCL-1)']);
		}).finally(()=>{
			setLoading(false);
		});
	};

	var onShowReject = (budget: Budget)=>{
		setSelectedBudget(budget);
		setRejectReasonId(null);
		setModalPrompts(null);
		setShowRejectModal(true);
	};

	var rejectBudget = (setLoading: SetLoading)=>{
		if(!rejectReasonId){
			setModalPrompts(['Selecciona una razón de rechazo']);
			return;
		}
		setLoading(true);
		API.rejectBudget(selectedBudget.budget_id, rejectReasonId).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success('Se ha rechazado el presupuesto');
			var reasonName = rejectReasons.find(r => r.reason_id === rejectReasonId)?.reason;
			setBudgets(prev => prev.map(b => 
				b.budget_id === selectedBudget.budget_id ? { ...b, rejected: true, approved: false, reject_reason_name: reasonName } : b
			));
			setShowRejectModal(false);
			setSelectedBudget(null);
		}).catch(err=>{
			setModalPrompts(['Hubo un error inesperado rechazando el presupuesto (LCL-1)']);
		}).finally(()=>{
			setLoading(false);
		});
	};

	var approveAllBudgets = (setLoading: SetLoading)=>{
		setLoading(true);
		var ids = budgets.filter(budget => !budget.approved).map(budget => budget.budget_id);
		API.approveBudget(ids).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success('Se han aprobado los presupuestos');

			setBudgets(prev => prev.map(b => ({ ...b, approved: true })));
			setShowApproveAllModal(false);
		}).catch(err=>{
			setModalPrompts(['Hubo un error inesperado actualizando los presupuestos (LCL-1)']);
		}).finally(()=>{
			setLoading(false);
		});
	};

	var budgetToExpense = (setLoading: SetLoading)=>{
		if(!selectedMethodId){
			return setModalPrompts(['Selecciona un método de pago']);
		}
		setLoading(true);
		API.convertToExpense(selectedBudget.budget_id, selectedBudget.account_id, selectedMethodId, selectedFile, fileTypeId, selectedBudget.budget_title, selectedBudget.description).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success('Se ha creado el gasto');

			setBudgets(prev => prev.map(b => 
				b.budget_id === selectedBudget.budget_id ? { ...b, expense_id: res.data.expense_id } : b
			));
			setShowGenerateExpenseModal(false);
			setSelectedBudget(null);
			setSelectedFile(null);
			setFileTypeId(null);
			if(uploadBoxRef.current){
				uploadBoxRef.current.clear();
			}
		}).catch(err=>{
			setModalPrompts(['Hubo un error inesperado creando el gasto (LCL-1)']);
		}).finally(()=>{
			setLoading(false);
		});
	};

	var deleteBudget = (setLoading: SetLoading)=>{
		setLoading(true);
		API.deleteBudget(selectedBudget.budget_id).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success('Se ha eliminado el gasto');

			setBudgets(prev => prev.filter(b => b.budget_id !== selectedBudget.budget_id));
			setShowDeleteModal(false);
			setSelectedBudget(null);
		}).catch(err=>{
			setModalPrompts(['Hubo un error inesperado eliminando el presupuesto (LCL-1)']);
		}).finally(()=>{
			setLoading(false);
		});
	};

	var onShowAccountsModal = async ()=>{
		API.getUserBankAccounts().then(res=>{
			if(res.error) return Toast.error(res.message);
			setAccounts(res.data);
			setShowAccountsModal(true);
		}).catch(err=>{
			return Toast.error('Hubo un error al obtener las cuentas (GET-1)');
		});
	};

	var onAccountSelected = (a: UserBankAccount)=>{
		var b = {...selectedBudget};
		b.account = a;
		b.account_id = a.account_id;
		setSelectedBudget(b);
		setShowAccountsModal(false);
		setSelectedFile(null);
		setFileTypeId(null);
		if(uploadBoxRef.current){
			uploadBoxRef.current.clear();
		}
		setShowGenerateExpenseModal(true);
	};

	var onFileSelected = (file: File)=>{
		var type : AssetType = null;
		if(!file){
			setSelectedFile(null);
			setFileTypeId(null);
			return;
		}
		if(file.type=='application/pdf'){
			type = AssetType.PDF;
		}else if(file.type.startsWith('image/')){
			type = AssetType.IMAGE;
		}
		if(!type) return Toast.error('El archivo no es un formato válido');
		setSelectedFile(file);
		setFileTypeId(type);
	}

	var onBudgetFormChange = bindFormChange(selectedBudget, setSelectedBudget);

	var countNotApprovedBudgets = budgets ? budgets.filter(b => !b.approved).length : 0;
	var budgetsTotal = budgets ? budgets.reduce((sum, b) => sum + (typeof b.amount === 'number' ? b.amount : parseFloat(b.amount) || 0), 0) : 0;

	return <>
		<Table
			style={{ maxWidth: 800, margin: 'auto' }}
			title='Presupuesto'
			striped
			headers={['ID', 'Titulo', 'Tipo', 'Departamento', 'Estatus', 'Monto', <Icon name='wrench' />]}
			centeredIndexes={[3, 5]}
			footer={
				(!!budgets && budgets.length >= PAGE_SIZE || page !== 0) && (
				<Table.Cell colSpan={8} row>
						<div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
							<Pagination page={page} onPageChange={onPageChange} hasNext={budgets.length>=PAGE_SIZE} />
						</div>
					</Table.Cell>
				)
			}
			button={<div>
			{countNotApprovedBudgets !== 0 && hasAccess(UserAccess.EVENTS.APPROVE_EXPENSE) ? (
				<Button 
					style={{ marginRight: 10 }} 
					text='Aprobar todos' 
					color='black' 
					iconName='check-circle' 
					size='tiny' 
					onClick={onShowApproveAll}
				/>
			) : null}
			{hasAccess(UserAccess.EVENTS.CREATE_BUDGET) && (
				<Button 
					text='Agregar' 
					color='black' 
					iconName='plus-circle' 
					size='tiny' 
					onClick={onAddBudget}
				/>
			)}
		</div>}
		>
			{loading ? (
				<Table.Row>
					<Table.Cell colSpan={8}>
						<Header loading text='Cargando datos...' />
					</Table.Cell>
				</Table.Row>
			) : budgets.length !== 0 ? (
				budgets.map(b => (
					<Table.Row
						key={b.budget_id}
						collapsingIndexes={[0, 2, 3, 4, 5, 6]}
						centeredIndexes={[5]}
						data={[
							b.budget_id,
							b.budget_title,
							b.type_name,
							b.department_id ? <Label style={{ width: 110 }} color='orange'>{getDepartmentName(b.department_id)}</Label> : <Icon name='minus'/>,
							<div className={classNames("fr label", {
								blue: b.approved && !b.deleted && !b.rejected && !b.paid,
								green: b.paid && !b.deleted,
								red: b.deleted || b.rejected,
								yellow: !b.approved && !b.deleted && !b.rejected && !b.paid
							})}>
								{b.deleted ? 'Eliminado' : b.rejected ? 'Rechazado' : b.paid ? 'Pagado' : b.approved ? 'Aprobado' : 'Pendiente'}
							</div>,
							'$' + addCommas(b.amount),
							<Dropdown direction='left' icon={(
								<div className="fr label" style={{ padding: '3px 10px' }}>
									<Icon name='ellipsis' style={{ margin: 0 }} />
								</div>
							)} className='icon'>
								<Dropdown.Menu direction='left'>
									<Dropdown.Item onClick={() => onShowInfo(b)}>
										<Icon name="eye" style={{ marginRight: 3 }} /> Info
									</Dropdown.Item>
									{!b.approved && !b.rejected && !b.paid && hasAccess(UserAccess.EVENTS.CREATE_BUDGET) ? (
										<Dropdown.Item onClick={() => onEditBudget(b)}>
											<Icon name="pencil" style={{ marginRight: 3 }} /> Editar
										</Dropdown.Item>
									) : null}
									{!b.approved && !b.rejected && !b.paid && hasAccess(UserAccess.EVENTS.APPROVE_EXPENSE) ? (
										<Dropdown.Item onClick={() => onShowApprove(b)}>
											<Icon name="check" style={{ marginRight: 3 }} /> Aprobar
										</Dropdown.Item>
									) : null}
									{!b.rejected && !b.deleted && !b.paid && hasAccess(UserAccess.EVENTS.REJECT_EXPENSE) ? (
										<Dropdown.Item onClick={() => { setSelectedBudget(b); setShowRejectModal(true); }}>
											<Icon name="ban" style={{ marginRight: 3 }} /> Rechazar
										</Dropdown.Item>
									) : null}
									{!b.expense_id && !b.deleted && b.approved && hasAccess(UserAccess.EVENTS.BUDGET_TO_EXPENSE) ? (
										<Dropdown.Item onClick={() => onShowGenerateExpense(b)}>
											<Icon name='money-bill' style={{ marginRight: 3 }} /> Generar gasto
										</Dropdown.Item>
									) : null}
									{!b.deleted && !b.paid && b.approved && !b.rejected && hasAccess(UserAccess.ACCOUNTING.PAY_EXPENSE) ? (
										<Dropdown.Item onClick={() => onShowPaid(b)}>
											<Icon name='check-circle' style={{ marginRight: 3 }} /> Marcar pagado
										</Dropdown.Item>
									) : null}
									{!b.expense_id && !b.deleted && !b.paid && hasAccess(UserAccess.EVENTS.DELETE_BUDGET) ? (
										<Dropdown.Item onClick={() => onShowDelete(b)}>
											<Icon name='close' style={{ marginRight: 3 }} /> Eliminar
										</Dropdown.Item>
									) : null}
									<Dropdown.Item onClick={() => onShowInfoLog(b)}>
										<Icon name="info" style={{ marginRight: 3 }} /> Ver Logs
									</Dropdown.Item>
								</Dropdown.Menu>
							</Dropdown>
						]}
					/>
				))
			) : (
				<Table.Cell row className='empty' colSpan={8}>
					No se encontraron registros
				</Table.Cell>
			)}
			{budgets.length !== 0 ? (
				<Table.Row>
					<Table.Cell colSpan={5} style={{ textAlign: 'end', fontSize: 14, fontWeight: 'bold' }}>
						Total:
					</Table.Cell>
					<Table.Cell collapsing colSpan={2} style={{ textAlign: 'start', fontSize: 14, fontWeight: 'bold' }}>
						${addCommas(budgetsTotal)}
					</Table.Cell>
				</Table.Row>
			) : null}
		</Table>
		<BudgetModal
			open={showBudgetModal}
			onClose={bindClose(setShowBudgetModal, false)}
			date_id={props.dateId}
			event_name={props.eventName}
			onCreate={onBudgetCreatedInternal}
			budgetToEdit={selectedBudget}
		/>
		<Modal open={showInfoModal} size='tiny' onClose={bindClick(setShowInfoModal, false)}>
			<Modal.Header>Información de presupuesto</Modal.Header>
			{selectedBudget &&<Modal.Content style={{ paddingBottom: 0 }}>
				<Table
					striped
					fitted
					details
					data={[
						['ID', selectedBudget.budget_id],
						['Título', selectedBudget.budget_title],
						['Tipo', selectedBudget.type_name],
						['Departamento', selectedBudget.department_id ? <Label style={{ width: 110 }} color='orange'>{getDepartmentName(selectedBudget.department_id)}</Label> : <Icon name='minus'/>],
						['Creador', selectedBudget.user_creator_name || <Icon name='minus'/>],
						...(selectedBudget.date_id ? [['Evento', selectedBudget.event_name || <Icon name='minus'/>]] : []),
						['Monto', selectedBudget.amount],
						['Agendamiento de Pago', selectedBudget.date_deposit ? moment.unix(selectedBudget.date_deposit).format('DD/MM/YYYY') : <Icon name='minus'/>],
						['Estatus', <div className={classNames("fr label", {
							blue: selectedBudget.approved && !selectedBudget.deleted && !selectedBudget.rejected && !selectedBudget.paid,
							green: selectedBudget.paid && !selectedBudget.deleted,
							red: selectedBudget.deleted || selectedBudget.rejected,
							yellow: !selectedBudget.approved && !selectedBudget.deleted && !selectedBudget.rejected && !selectedBudget.paid
						})}>
							{selectedBudget.deleted ? 'Eliminado' : selectedBudget.rejected ? 'Rechazado' : selectedBudget.paid ? 'Pagado' : selectedBudget.approved ? 'Aprobado' : 'Pendiente'}
						</div>],
						...(selectedBudget.rejected && !selectedBudget.deleted ? [['Razón de rechazo', selectedBudget.reject_reason_name || <Icon name='minus'/>]] : []),
						['Descripción', selectedBudget.description],
					]}
				/>
				<Message type="error" list={modalPrompts} />
			</Modal.Content>}
			<Modal.Actions>
				<Button basic text='Cerrar' onClick={bindClick(setShowInfoModal, false)} />
			</Modal.Actions>
		</Modal>
		<Modal open={showApproveModal} onClose={bindClick(setShowApproveModal, false)} size='tiny'>
			<Modal.Header>¿Aprobar presupuesto?</Modal.Header>
			{selectedBudget && <Modal.Content>
				<Header text='¿Marcar presupuesto como aprobado?'/>
				<Table 
					fitted
					details
					striped
					data={[
						['ID', selectedBudget.budget_id],
						['Titulo', selectedBudget.budget_title],
						['Tipo', selectedBudget.type_name],					
						['Departamento', selectedBudget.department_name || <Icon name='minus'/>],						
						['Monto', selectedBudget.amount],
						['Descripción', selectedBudget.description]
					]}
				/>
				<Message type="error" list={modalPrompts} />
			</Modal.Content>}
			<Modal.Actions>
				<Button text='Cerrar' basic onClick={bindClick(setShowApproveModal, false)} />
				<Button text='Aprobar' color='black' onClick={approveBudget}/>
			</Modal.Actions>
		</Modal>
		<Modal open={showGenerateExpenseModal} onClose={bindClick(setShowGenerateExpenseModal, false)} size='tiny'>
			<Modal.Header>¿Generar gasto?</Modal.Header>
			{selectedBudget && <Modal.Content>
				<Header text='¿Generar gasto desde presupuesto?'/>
				<Table 
					details
					striped
					data={[
						['ID', selectedBudget.budget_id],
						['Titulo', selectedBudget.budget_title],
						['Tipo', selectedBudget.type_name],
						['Departamento', selectedBudget.department_name ? <Label style={{ width: 100 }} color='orange'>{getDepartmentName(selectedBudget.department_id)}</Label> : <Icon name='minus'/>],
						['Monto', selectedBudget.amount],
						['Descripción', selectedBudget.description]
					]}
				/>
				<Checkbox label='Es una reposición'checked={selectedBudget.requiere_date_id} onChange={onBudgetFormChange('requiere_date_id')} style={{ marginTop: 15, marginBottom: 15 }}/>
				{!!selectedBudget.requiere_date_id && (
					<Field label='Cuenta bancaria'>
						<Input
							value={[selectedBudget.account?.name_account, selectedBudget.account?.bank].filter(Boolean).join(' - ')}
							placeholder='Selecciona una cuenta...'
							readonly
							button={
								<Button
									text='Seleccionar'
									onClick={onShowAccountsModal}
								/>
							}
						/>
					</Field>
				)}
				<Field label='Método de pago'>
					<Dropdown 
						selection 
						value={selectedMethodId} 
						onChange={(e, { value })=>setSelectedMethodId(value as number)} 
						placeholder='Método de pago' 
						options={paymentMethods.map(m=>({
							text: m.method_name,
							value: m.method_id,
							key: m.method_id
						}))} 
					/>
				</Field>
				<Field label='Archivo (Opcional)'>
					<UploadBox
						ref={uploadBoxRef}
						icon={getAssetTypeIcon(fileTypeId)}
						type={'normal'}
						text={selectedFile ? selectedFile.name : null}
						maxFileSize={2.5*1024*1024}
						onFile={onFileSelected}
					/>
				</Field>
				<Message type="error" list={modalPrompts} />
			</Modal.Content>}
			<Modal.Actions>
				<Button text='Cerrar' basic onClick={bindClick(setShowGenerateExpenseModal, false)} />
				<Button text='Generar' color='black' onClick={budgetToExpense}/>
			</Modal.Actions>
		</Modal>	
		<Modal open={showDeleteModal} onClose={bindClick(setShowDeleteModal, false)} size='tiny'>
			<Modal.Header>¿Eliminar presupuesto?</Modal.Header>
			{selectedBudget && <Modal.Content>
				<Table 
					details
					striped
					data={[
						['ID', selectedBudget.budget_id],
						['Titulo', selectedBudget.budget_title],
						['Tipo', selectedBudget.type_name],					
						['Departamento', selectedBudget.department_name ? <Label>{selectedBudget.department_name}</Label> : <Icon name='minus'/>],						
						['Monto', selectedBudget.amount],
						['Descripción', selectedBudget.description]
					]}
				/>
				<Message type="error" list={modalPrompts} />
			</Modal.Content>}
			<Modal.Actions>
				<Button text='Cerrar' basic onClick={bindClick(setShowDeleteModal, false)} />
				<Button text='Eliminar' color='red' onClick={deleteBudget}/>
			</Modal.Actions>
		</Modal>
		<Modal open={showPaidModal} onClose={bindClick(setShowPaidModal, false)} size='tiny'>
			<Modal.Header>¿Marcar presupuesto como pagado?</Modal.Header>
			{selectedBudget && <Modal.Content>
				<Header text='¿Marcar este presupuesto como pagado?'/>
				<Table 
					details
					striped
					data={[
						['ID', selectedBudget.budget_id],
						['Titulo', selectedBudget.budget_title],
						['Tipo', selectedBudget.type_name],
						['Departamento', selectedBudget.department_name ? <Label style={{ width: 100 }} color='orange'>{getDepartmentName(selectedBudget.department_id)}</Label> : <Icon name='minus'/>],
						['Monto', '$' + addCommas(selectedBudget.amount)],
					]}
				/>
				<Message type="error" list={modalPrompts} />
			</Modal.Content>}
			<Modal.Actions>
				<Button text='Cerrar' basic onClick={bindClick(setShowPaidModal, false)} />
				<Button text='Marcar pagado' color='black' onClick={paidBudget}/>
			</Modal.Actions>
		</Modal>
		<Modal open={showApproveAllModal} onClose={bindClick(setShowApproveAllModal, false)} size='tiny'>
			<Modal.Header>¿Aprobar presupuestos?</Modal.Header>
			<Modal.Content>
				<Header text='¿Marcar presupuestos como aprobados?'/>
				<Table 
					details
					striped
					data={[
						['Presupuestos', countNotApprovedBudgets],
						['Total', '$' + addCommas(budgetsTotal)],
					]}
				/>
				<Message type="error" list={modalPrompts} />
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cerrar' basic onClick={bindClick(setShowApproveAllModal, false)} />
				<Button text='Aprobar' color='black' onClick={approveAllBudgets}/>
			</Modal.Actions>
		</Modal>
		<Modal open={showAccountsModal} onClose={bindClick(setShowAccountsModal, false)} size='mini'>
			<Modal.Header>Selecciona la cuenta</Modal.Header>
			<Modal.Content>
				<Items
					selectable
					data={accounts}
					onSelected={(a)=>onAccountSelected(a)}
					render={a=>{
						return <div className="ar card placeholder account">
							<Icon name={a.clabe ? 'landmark' : 'credit-card'} />
							<div className="left">
								<div className="title">
									{a.clabe ? <>
										<b>SPEI</b> **{a.clabe.slice(-5)}
									</> : <>
										<b>{a.bank}</b> **{a.card_number.slice(-4)}
									</>}
								</div>
								<div className="name">{a.name_account}</div>
							</div>
						</div>
					}}
				/>
				<Items
					data={[0]}
					selectable
					render={()=>{
						return <Link to={'/accounts'} target='_blank' style={{ textDecoration: 'none', color: 'inherit', width: '100%' }}>
							<div style={{ fontSize: 18, fontWeight: 'bold', textAlign: 'center', padding: 5 }}>Agregar Cuenta</div>
						</Link> 
					}}
				/>
			</Modal.Content>
			<Modal.Actions>
				<Button basic text='Cerrar' onClick={bindClick(setShowAccountsModal, false)} />
			</Modal.Actions>
		</Modal>
		<InfoLog
			open={showInfoLogModal}
			onClose={() => setShowInfoLogModal(false)}
			data={selectedBudget}
			title="Historial del Presupuesto"
		/>
		<Modal open={showRejectModal} onClose={bindClick(setShowRejectModal, false)} size='tiny'>
			<Modal.Header>Rechazar presupuesto</Modal.Header>
			<Modal.Content>
				<Header text='¿Rechazar este presupuesto?' subtext='El presupuesto será marcado como rechazado y se notificará al creador con el motivo de rechazo.' />
				<Dropdown
					selection
					fluid
					placeholder='Selecciona el motivo de rechazo'
					value={rejectReasonId}
					onChange={(e, { value }) => setRejectReasonId(value as number)}
					options={rejectReasons.map(r => ({
						key: r.reason_id,
						value: r.reason_id,
						text: r.reason,
					}))}
				/>
				<Message type="error" list={modalPrompts} />
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cerrar' basic onClick={bindClick(setShowRejectModal, false)} />
				<Button onClick={rejectBudget} text='Rechazar' color='red'/>
			</Modal.Actions>
		</Modal>
	</>
};

export default BudgetTable;